/**
 * Quick View — Graedance
 *
 * Floating + button on product cards.
 * Multi-variant: opens centered modal with images, details, variant picker.
 * Single-variant: adds to cart directly.
 * Sold-out: button hidden (handled in Liquid).
 */

const QV = {
  wrapper: null,
  backdrop: null,
  modal: null,
  body: null,
  isOpen: false,

  /** Build the modal shell once, reuse it */
  init() {
    if (this.wrapper) return;

    // Wrapper: fixed full-screen container
    this.wrapper = document.createElement('div');
    this.wrapper.className = 'qv-wrapper';
    this.wrapper.setAttribute('role', 'dialog');
    this.wrapper.setAttribute('aria-modal', 'true');

    // Backdrop: separate element behind modal
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'qv-backdrop';

    // Modal: the actual panel
    this.modal = document.createElement('div');
    this.modal.className = 'qv-modal';
    this.modal.innerHTML = `
      <button class="qv-close" aria-label="Close">
        <svg viewBox="0 0 16 16"><path d="M2 2l12 12M14 2L2 14"/></svg>
      </button>
      <div class="qv-modal-body"></div>`;

    this.wrapper.appendChild(this.backdrop);
    this.wrapper.appendChild(this.modal);
    document.body.appendChild(this.wrapper);

    this.body = this.modal.querySelector('.qv-modal-body');

    // Close on backdrop click
    this.backdrop.addEventListener('click', () => this.close());

    // Close on X button
    this.modal.querySelector('.qv-close').addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Intercept modal form submit — silent add via fetch (no drawer)
    this.modal.addEventListener('submit', async (e) => {
      e.preventDefault();
      const form = e.target.closest('[data-qv-add-form]');
      if (!form) return;

      const addBtn = this.body.querySelector('.qv-add-btn');
      // Get variant ID from the hidden input or select
      const idInput = form.querySelector('input[name="id"], select[name="id"]');
      const variantId = idInput ? idInput.value : (addBtn ? addBtn.dataset.variantId : null);
      if (!variantId) return;

      // Show "Adding..."
      if (addBtn) {
        addBtn.setAttribute('data-state', 'adding');
        addBtn.disabled = true;
        addBtn.querySelector('[data-button-text]').textContent = 'Adding...';
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
        });

        if (res.ok) {
          // Refresh cart sections (counter, etc) without opening drawer
          this._refreshCartSections();

          // Show "Added!" then close
          if (addBtn) {
            addBtn.setAttribute('data-state', 'added');
            addBtn.querySelector('[data-button-text]').textContent = 'Added';
          }
          setTimeout(() => this.close(), 1200);
        } else {
          const err = await res.json();
          if (addBtn) {
            const msg = err.description || 'Error';
            const shortMsg = msg.includes('are in your cart') ? 'All in cart' : msg.includes('availability') ? 'Max in cart' : msg.length > 20 ? msg.slice(0, 18) + '…' : msg;
            addBtn.setAttribute('data-state', 'error');
            addBtn.disabled = false;
            addBtn.querySelector('[data-button-text]').textContent = shortMsg;
          }
        }
      } catch (err) {
        if (addBtn) {
          addBtn.removeAttribute('data-state');
          addBtn.disabled = false;
          addBtn.querySelector('[data-button-text]').textContent = 'Add to Cart';
        }
        console.warn('Quick View: add to cart failed', err);
      }
    });
  },

  /**
   * Refresh all [data-ajax-cart-section] elements via Shopify Section Rendering API.
   * This updates the cart counter / badge without opening the drawer.
   */
  async _refreshCartSections() {
    try {
      // Find all cart section elements that liquid-ajax-cart manages
      const sectionEls = document.querySelectorAll('[data-ajax-cart-section]');
      if (!sectionEls.length) return;

      // Extract section IDs from their parent shopify-section wrapper
      const sectionIds = [];
      sectionEls.forEach(el => {
        const wrapper = el.closest('[id^="shopify-section-"]');
        if (wrapper) {
          sectionIds.push(wrapper.id.replace('shopify-section-', ''));
        }
      });

      if (!sectionIds.length) return;

      // Fetch updated section HTML from Shopify
      const res = await fetch('/?sections=' + sectionIds.join(','));
      if (!res.ok) return;
      const sections = await res.json();

      // Replace each section's HTML in the DOM
      for (const [id, html] of Object.entries(sections)) {
        const container = document.getElementById('shopify-section-' + id);
        if (container) {
          container.innerHTML = html;
        }
      }
    } catch (err) {
      console.warn('Quick View: cart section refresh failed', err);
    }
  },

  /** Open modal and load content for a product */
  async open(handle, url) {
    this.init();
    this.body.innerHTML = '';

    try {
      const sectionUrl = `${url.split('?')[0]}?section_id=x-quickview`;
      const res = await fetch(sectionUrl);
      const html = await res.text();
      this.body.innerHTML = html;
      this._upgradeComponents();

      // Show modal only after content is ready
      this.wrapper.classList.add('qv-open');
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
    } catch (err) {
      console.warn('Quick View: failed to load', err);
    }
  },

  /** Close the modal */
  close() {
    if (!this.wrapper) return;
    this.wrapper.classList.remove('qv-open');
    this.isOpen = false;
    document.body.style.overflow = '';
    // Clean up body after transition
    setTimeout(() => {
      if (!this.isOpen && this.body) this.body.innerHTML = '';
    }, 350);
  },

  /** Trigger custom element upgrades for dynamically inserted HTML */
  _upgradeComponents() {
    // Gallery arrow navigation
    this._initGallery();

    // Variant picker: listen for changes to reload content
    const picker = this.body.querySelector('coretex-variant-picker');
    if (picker) {
      picker.addEventListener('variant:change', async (e) => {
        const { targetUrl, optionValues } = e.detail;
        if (!targetUrl) return;

        this.body.setAttribute('data-loading', 'true');
        let url = targetUrl.split('?')[0] + '?section_id=x-quickview';
        if (optionValues) url += '&option_values=' + encodeURIComponent(optionValues);

        try {
          const res = await fetch(url);
          const html = await res.text();
          this.body.removeAttribute('data-loading');
          this.body.innerHTML = html;
          this._upgradeComponents();
        } catch (err) {
          this.body.removeAttribute('data-loading');
          console.warn('Quick View: variant reload failed', err);
        }
      });
    }
  },

  /** Initialize gallery arrow navigation */
  _initGallery() {
    const gallery = this.body.querySelector('.qv-gallery');
    if (!gallery) return;

    const items = gallery.querySelectorAll('.qv-gallery-item');
    const prevBtn = gallery.querySelector('.qv-arrow--prev');
    const nextBtn = gallery.querySelector('.qv-arrow--next');
    const counter = gallery.querySelector('[data-current]');
    if (items.length <= 1) return;

    let current = 0;

    const show = (index) => {
      items.forEach(item => item.classList.remove('active'));
      items[index].classList.add('active');
      current = index;
      if (prevBtn) prevBtn.disabled = current === 0;
      if (nextBtn) nextBtn.disabled = current === items.length - 1;
      if (counter) counter.textContent = current + 1;
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (current > 0) show(current - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (current < items.length - 1) show(current + 1);
      });
    }

    show(0);
  },

  /** Direct add-to-cart for single-variant products — no drawer, just counter update */
  async directAdd(variantId, btn) {
    // Show immediate feedback
    btn.setAttribute('data-added', 'true');
    btn.innerHTML = `<svg viewBox="0 0 16 16" style="width:12px;height:12px;stroke-width:1.75;fill:none"><polyline points="3 8 7 12 13 4"/></svg><span class="qv-trigger-text">Adding</span>`;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      if (res.ok) {
        // Refresh cart sections (counter, etc) without opening drawer
        this._refreshCartSections();

        // Show "Added"
        btn.innerHTML = `<svg viewBox="0 0 16 16" style="width:12px;height:12px;stroke-width:1.75;fill:none"><polyline points="3 8 7 12 13 4"/></svg><span class="qv-trigger-text">Added</span>`;
        setTimeout(() => {
          btn.removeAttribute('data-added');
          btn.innerHTML = `<svg viewBox="0 0 16 16"><line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/></svg><span class="qv-trigger-text">Add</span>`;
        }, 1800);
      } else {
        // Inventory exceeded or other error — show Shopify's message
        const errData = await res.json().catch(() => null);
        const msg = errData?.description || 'Unavailable';
        // Shorten common Shopify message for the small pill
        const shortMsg = (msg.includes('are in your cart') || msg.includes('was added') || msg.includes('were added')) ? 'All in cart' : msg.length > 16 ? msg.slice(0, 14) + '…' : msg;
        btn.innerHTML = `<svg viewBox="0 0 16 16" style="width:12px;height:12px;stroke-width:1.75;fill:none"><line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/></svg><span class="qv-trigger-text">${shortMsg}</span>`;
        setTimeout(() => {
          btn.removeAttribute('data-added');
          btn.innerHTML = `<svg viewBox="0 0 16 16"><line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/></svg><span class="qv-trigger-text">Add</span>`;
        }, 3000);
      }
    } catch (err) {
      btn.removeAttribute('data-added');
      btn.innerHTML = `<svg viewBox="0 0 16 16"><line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/></svg><span class="qv-trigger-text">Add</span>`;
      console.warn('Quick View: add to cart failed', err);
    }
  }
};

// Delegate click handling for all quick view triggers
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.qv-trigger');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const variantId = btn.dataset.variantId;
  const productUrl = btn.dataset.productUrl;
  const productHandle = btn.dataset.productHandle;
  const hasVariants = btn.dataset.hasVariants === 'true';

  if (!hasVariants && variantId) {
    // Single variant: add directly
    QV.directAdd(parseInt(variantId), btn);
  } else {
    // Multi-variant: open quick view modal
    QV.open(productHandle, productUrl);
  }
});
